import { Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export interface ToastProps {
  show: boolean;
  variant: 'success' | 'error';
  message: string;
  onClose: () => void;
}

const AUTO_DISMISS_MS = 4000;

export function Toast({ show, variant, message, onClose }: ToastProps) {
  useEffect(() => {
    if (!show) return;
    const t = window.setTimeout(onClose, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [show, onClose]);

  const Icon = variant === 'success' ? CheckCircleIcon : ExclamationCircleIcon;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed top-6 right-6 z-[70] flex justify-end"
    >
      <Transition
        as={Fragment}
        show={show}
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={clsx(
            'pointer-events-auto w-full max-w-sm overflow-hidden rounded-md border shadow-lg',
            // Theme-aware via CSS vars — defaults preserve today's dark
            // appearance, the future light theme inherits cleanly.
            'bg-[var(--dv-bg-card-solid,#171717)] text-[var(--dv-text-primary,#f5f5f5)]',
            variant === 'success' ? 'border-cyan-500/30' : 'border-red-500/40'
          )}
        >
          <div className="p-4 flex items-start gap-3">
            <Icon
              className={clsx(
                'h-5 w-5 shrink-0',
                variant === 'success' ? 'text-cyan-400' : 'text-red-400'
              )}
              aria-hidden="true"
            />
            <p className="flex-1 text-sm text-[var(--dv-text-primary,#f5f5f5)]">{message}</p>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 text-[var(--dv-text-muted,#737373)] hover:text-[var(--dv-text-secondary,#a3a3a3)]"
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
}
